import React, { useMemo, useState, useEffect } from "react";
import ReactPaginate from "react-paginate";
import _ from "lodash";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import * as CountryLanguage from "@ladjs/country-language";
import CountryLanguageModal from "./CountryLanguageModal";
import {
  addCountryLanguage,
  checkCountryLanguageUsed,
  deleteCountryLanguage,
  fetchCountryLanguages,
  updateCountryLanguage,
} from "../../store/reducers/countryLanguageSlice";
import {
  setCountryLanguageDeleteModal,
  setCountryLanguageModal,
} from "../../store/reducers/modalSlice";

const emptyForm = {
  country: "",
  language: "",
  combined_code: "",
};

const PROTECTED_COUNTRY_LANGUAGE_CODE = "USA-EN";

const isProtectedCountryLanguage = (item) =>
  item?.combined_code === PROTECTED_COUNTRY_LANGUAGE_CODE;

const getLanguageName = (language) => {
  if (!language) return "";
  if (Array.isArray(language.name)) return language.name[0] || "";
  return language.name || "";
};

const getLanguageCode = (language) => {
  if (!language) return "";
  return language.iso639_1 || language.iso639_2en || language.iso639_2 || language.iso639_3 || "";
};

const buildCombinedCode = (country, language) => {
  if (!country || !language) return "";

  const countryCode = country.code_3 || country.code_2 || "";
  const languageCode = getLanguageCode(language);

  if (!countryCode || !languageCode) return "";

  return `${countryCode.toUpperCase()}-${languageCode.toUpperCase()}`;
};

const getApiErrorMessage = (error, fallbackMessage) => {
  if (typeof error === "string") return error;
  return error?.error || error?.message || fallbackMessage;
};

const CountryLanguageSection = () => {
  const itemsPerPage = 10;
  const dispatch = useDispatch();
  const {
    countryLanguages,
    total,
    loading,
    saving,
    deleting,
    checkingUsed,
    isCountryLanguageUsed,
  } = useSelector((state) => state.countryLanguage);
  const { countryLanguageModal, countryLanguageDeleteModal } = useSelector(
    (state) => state.modal
  );

  const countries = useMemo(
    () =>
      CountryLanguage.getCountries()
        .filter((country) => country.name && country.code_3)
        .sort((a, b) => a.name.localeCompare(b.name)),
    []
  );

  const allLanguages = useMemo(
    () =>
      CountryLanguage.getLanguages()
        .filter((language) => getLanguageName(language))
        .sort((a, b) => getLanguageName(a).localeCompare(getLanguageName(b))),
    []
  );

  const [countryLanguageFilter, setCountryLanguageFilter] = useState("");
  const [debouncedCountryLanguage, setDebouncedCountryLanguage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCountryLanguageId, setSelectedCountryLanguageId] =
    useState(null);
  const [checkingCountryLanguageId, setCheckingCountryLanguageId] =
    useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState(emptyForm);

  const pageCount = Math.ceil(total / itemsPerPage);

  const loadCountryLanguages = (pageNo = 1, search = "") => {
    dispatch(
      fetchCountryLanguages({
        page: pageNo,
        limit: itemsPerPage,
        search,
      })
    );
  };

  useEffect(() => {
    const handler = _.debounce((value) => {
      setDebouncedCountryLanguage(value);
      setCurrentPage(1);
    }, 500);

    handler(countryLanguageFilter);

    return () => handler.cancel();
  }, [countryLanguageFilter]);

  useEffect(() => {
    loadCountryLanguages(currentPage, debouncedCountryLanguage);
  }, [currentPage, debouncedCountryLanguage]);

  const resolveCombinedCode = (countryName, languageName) => {
    if (!countryName || !languageName) return "";

    const country = countries.find((item) => item.name === countryName);
    const language = allLanguages.find(
      (item) => getLanguageName(item) === languageName
    );
    return buildCombinedCode(country, language);
  };

  const openAddModal = () => {
    setEditingItem(null);
    setFormError("");
    setForm(emptyForm);
    dispatch(setCountryLanguageModal(true));
  };

  const openEditModal = (item) => {
    if (isProtectedCountryLanguage(item)) return;

    setEditingItem(item);
    setFormError("");
    setForm({
      country: item.country || "",
      language: item.language || "",
      combined_code: item.combined_code || "",
    });
    dispatch(setCountryLanguageModal(true));
  };

  const closeFormModal = () => {
    if (saving) return;
    dispatch(setCountryLanguageModal(false));
    setEditingItem(null);
    setFormError("");
    setForm(emptyForm);
  };

  const handlePageClick = (event) => {
    const selectedPage = event.selected + 1;
    setCurrentPage(selectedPage);
  };

  const handleCountryChange = (e) => {
    const nextCountry = e.target.value;
    setFormError("");
    const nextCode = resolveCombinedCode(nextCountry, form.language);

    setForm({
      country: nextCountry,
      language: form.language,
      combined_code: nextCode,
    });
  };

  const handleLanguageChange = (e) => {
    const nextLanguage = e.target.value;
    setFormError("");
    const nextCode = resolveCombinedCode(form.country, nextLanguage);

    setForm((currentForm) => ({
      ...currentForm,
      language: nextLanguage,
      combined_code: nextCode,
    }));
  };

  const handleCodeChange = (e) => {
    setFormError("");
    setForm((currentForm) => ({
      ...currentForm,
      combined_code: e.target.value.toUpperCase(),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.country || !form.language || !form.combined_code) {
      setFormError("All fields are required");
      return;
    }

    setFormError("");

    try {
      if (editingItem?.id) {
        await dispatch(
          updateCountryLanguage({ id: editingItem.id, data: form })
        ).unwrap();
        toast.success("Country language updated successfully");
      } else {
        await dispatch(addCountryLanguage(form)).unwrap();
        toast.success("Country language added successfully");
      }

      closeFormModal();
      loadCountryLanguages(currentPage, debouncedCountryLanguage);
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, "Save failed");
      setFormError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleDeleteClick = async (id) => {
    const item = countryLanguages.find(
      (countryLanguage) => countryLanguage.id === id,
    );
    if (isProtectedCountryLanguage(item)) return;

    setSelectedCountryLanguageId(id);
    setCheckingCountryLanguageId(id);

    try {
      await dispatch(checkCountryLanguageUsed(id)).unwrap();
      dispatch(setCountryLanguageDeleteModal(true));
    } catch (error) {
      toast.error("Unable to check country language usage");
    } finally {
      setCheckingCountryLanguageId(null);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await dispatch(deleteCountryLanguage(selectedCountryLanguageId)).unwrap();
      toast.success("Country language deleted successfully");
      dispatch(setCountryLanguageDeleteModal(false));
      loadCountryLanguages(currentPage, debouncedCountryLanguage);
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  return (
    <>
      <div className="row my-2 align-items-center">
        <div className="col-12 d-flex flex-column flex-md-row align-items-center justify-content-between">
          <div className="fw-bold mb-2 mb-md-0" style={{ minWidth: 170 }}>
            Countries/Languages
          </div>
          <div
            className="d-flex justify-content-center align-items-center gap-2 mx-auto"
            style={{ maxWidth: 600, width: "100%" }}
          >
            <input
              type="text"
              className="form-control"
              style={{ minWidth: 300 }}
              placeholder="Search by country, language or code"
              value={countryLanguageFilter}
              onChange={(e) => setCountryLanguageFilter(e.target.value)}
            />

            {countryLanguageFilter && (
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => setCountryLanguageFilter("")}
              >
                Clear
              </button>
            )}
          </div>

          <button className="btn bg_color" type="button" onClick={openAddModal}>
            Add New
          </button>
        </div>
      </div>

      <table className="table align-middle">
        <thead>
          <tr>
            <th>Sr. No</th>
            <th>Country</th>
            <th>Language</th>
            <th>Combined Code</th>
            <th style={{ width: "70px", textAlign: "center" }}>Edit</th>
            <th style={{ width: "80px", textAlign: "center" }}>Remove</th>
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr>
              <td colSpan="6" className="text-center">
                Loading...
              </td>
            </tr>
          ) : (
            countryLanguages?.map((item, i) => {
              const protectedCountryLanguage =
                isProtectedCountryLanguage(item);

              return (
                <tr key={item.id || `${item.country}-${item.language}-${i}`}>
                  <td>{i + 1 + itemsPerPage * (currentPage - 1)}</td>
                  <td>{item.country}</td>
                  <td>{item.language}</td>
                  <td>{item.combined_code}</td>
                  <td className="text-center">
                    <button
                      className="btn btn-sm bg_color"
                      onClick={() => openEditModal(item)}
                      disabled={protectedCountryLanguage}
                      title={
                        protectedCountryLanguage
                          ? "Default country language cannot be edited"
                          : "Edit"
                      }
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                  </td>
                  <td className="text-center">
                    <button
                      className="btn btn-sm bg_color country-language-delete-btn"
                      onClick={() => handleDeleteClick(item.id)}
                      disabled={
                        protectedCountryLanguage ||
                        (checkingUsed && checkingCountryLanguageId === item.id)
                      }
                      title={
                        protectedCountryLanguage
                          ? "Default country language cannot be deleted"
                          : "Delete"
                      }
                    >
                      <i
                        className={
                          checkingUsed && checkingCountryLanguageId === item.id
                            ? "fas fa-spinner fa-spin"
                            : "fas fa-trash"
                        }
                      ></i>
                    </button>
                  </td>
                </tr>
              );
            })
          )}

          <tr hidden={countryLanguages?.length !== 0 || loading}>
            <td colSpan="6" className="text-center">
              Data Not Found
            </td>
          </tr>
        </tbody>
      </table>

      <ReactPaginate
        nextLabel="Next"
        previousLabel="Previous"
        onPageChange={handlePageClick}
        pageCount={pageCount}
        forcePage={currentPage - 1}
        pageRangeDisplayed={5}
        pageClassName="page-item"
        pageLinkClassName="page-link"
        previousClassName="page-item"
        previousLinkClassName="page-link"
        nextClassName="page-item"
        nextLinkClassName="page-link"
        breakLabel="..."
        breakClassName="page-item"
        breakLinkClassName="page-link"
        containerClassName="pagination"
        activeClassName="active"
      />

      <CountryLanguageModal
        show={countryLanguageModal}
        editingItem={editingItem}
        form={form}
        countries={countries}
        languages={allLanguages}
        saving={saving}
        combinedCodeLoading={false}
        error={formError}
        onClose={closeFormModal}
        onSubmit={handleSubmit}
        onCountryChange={handleCountryChange}
        onLanguageChange={handleLanguageChange}
        onCodeChange={handleCodeChange}
        getLanguageName={getLanguageName}
        getLanguageCode={getLanguageCode}
      />

      <Modal
        show={countryLanguageDeleteModal}
        onHide={() => dispatch(setCountryLanguageDeleteModal(false))}
        centered
        size="ml"     >
        <Modal.Header closeButton className="px-3 py-2">
          <Modal.Title className="text-base">
            Delete country language?
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="px-3 py-3">
          {isCountryLanguageUsed && (
            <div className="country-language-delete-warning">
              This country language is used. Deleting it may affect existing data.
            </div>
          )}
          <div className={isCountryLanguageUsed ? "mt-2" : ""}>
            Are you sure you want to delete this country language?
          </div>
        </Modal.Body>

        <Modal.Footer className="px-3 py-2">
          <Button
            variant="secondary"
            size="sm"
            className="country-language-modal-btn country-language-cancel-btn"
            onClick={() => dispatch(setCountryLanguageDeleteModal(false))}
          >
            Cancel
          </Button>

          <Button
            variant="primary"
            size="sm"
            className="country-language-modal-btn country-language-confirm-btn"
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CountryLanguageSection;
